import { css } from '@emotion/react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';
import type { RefObject } from 'react';
import {
  useState,
  useEffect,
  createRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import type {
  DayPickerProps,
  AfterModifier,
  BeforeModifier,
} from 'react-day-picker';
import { DateUtils } from 'react-day-picker';
import { Icon } from '@components/Icon/Icon';
import { IconButton } from '@components/IconButton/IconButton';
import { TextInput } from '@components/Inputs/TextInput/TextInput';
import type { TextInputProps } from '@components/Inputs/TextInput/TextInput';

import DPI from 'react-day-picker/DayPickerInput';
import { useStrings } from '@hooks/useStrings';

// @ts-expect-error Workaround for DPI issue with esm builds https://github.com/gpbl/react-day-picker/issues/1194
const DayPickerInput = DPI.__esModule ? (DPI.default as typeof DPI) : DPI;

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DatePickerInputProps {
  /**
   * The unique ID (attribute) of the html input element.
   */
  id?: string;
  /**
   * Name of the input, for form purposes.
   */
  name?: string;
  /**
   * Size of the input. 2 sizes, to result in 32/40 px height.
   */
  size?: 's' | 'm';
  /**
   * Should the date picker prevent picking a date in the future.
   */
  preventFutureDate?: boolean;
  /**
   * Prevents date selection before a given date value. Date object.
   */
  preventDatesBefore?: Date;
  /**
   * Value of the date. string as YYYY-M-D or Date object.
   */
  value?: string | Date;
  /**
   * Callback when date is changed, either via the input or the picker.
   */
  onDateChange?: (value: Date | undefined) => void;
  /**
   * Specifies if the input is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the input is invalid. When set to true, we'll render the input in invalid state.
   */
  isInvalid?: boolean;
  /**
   * Reference to the actual input html element.
   */
  inputRef?: RefObject<HTMLInputElement>;
  /**
   * Additional props to the dayPicker component.
   */
  dayPickerProps?: DayPickerProps;
}

interface PickerInputProps extends TextInputProps {
  inputRef?: RefObject<HTMLInputElement>;
}

interface NavbarElementProps {
  nextMonth: Date;
  previousMonth: Date;
  onPreviousClick: () => void;
  onNextClick: () => void;
  preventFutureDate?: boolean;
}

const NavbarElement = ({
  nextMonth,
  onPreviousClick,
  onNextClick,
  preventFutureDate,
}: NavbarElementProps) => {
  const [showNextMonth, setShowNextMonth] = useState(true);
  const { datePicker } = useStrings();

  useEffect(() => {
    if (preventFutureDate) {
      setShowNextMonth(!DateUtils.isFutureDay(nextMonth));
    }
  }, [nextMonth]);
  return (
    <>
      <IconButton
        icon="chevron_left"
        data-testid="prev-month"
        tooltipText={datePicker.previousMonthButtonTooltip}
        kind="ghost"
        size="s"
        onClick={() => {
          onPreviousClick();
        }}
        css={css`
          position: absolute;
          top: ${tokens.spacer.x2};
          left: ${tokens.spacer.x2};
        `}
      />
      {showNextMonth && (
        <IconButton
          icon="chevron_right"
          tooltipText={datePicker.nextMonthButtonTooltip}
          kind="ghost"
          size="s"
          onClick={() => {
            onNextClick();
          }}
          css={css`
            position: absolute;
            top: ${tokens.spacer.x2};
            right: ${tokens.spacer.x2};
          `}
        />
      )}
    </>
  );
};

const CalendarButton = styled.button`
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

const PickerInput = forwardRef(
  ({ inputRef, ...props }: PickerInputProps, ref): JSX.Element => {
    inputRef = inputRef || createRef<HTMLInputElement>();

    useImperativeHandle(
      ref,
      () =>
        ({
          focus: () => {
            inputRef.current.focus();
          },
        } as HTMLInputElement),
    );

    return (
      <TextInput
        {...props}
        autoComplete="off"
        ref={inputRef}
        actionContent={
          <CalendarButton
            onClick={() => {
              inputRef.current.focus();
            }}
          >
            <Icon icon="date" color="text_disabled" />
          </CalendarButton>
        }
      />
    );
  },
);

PickerInput.displayName = 'PickerInput';

export const DatePickerInput = ({
  id,
  name,
  size = 'm',
  preventFutureDate = false,
  preventDatesBefore,
  value,
  onDateChange,
  isDisabled,
  isInvalid,
  inputRef,
  dayPickerProps,
}: DatePickerInputProps): JSX.Element => {
  const [today] = useState(new Date());
  const [disabledDays, setDisabledDays] =
    useState<DayPickerProps['disabledDays']>(undefined);

  useEffect(() => {
    const hasValidPreventDatesBefore = DateUtils.isDate(preventDatesBefore);

    if (!preventFutureDate && !hasValidPreventDatesBefore) {
      setDisabledDays(dayPickerProps?.disabledDays);
      return;
    }
    setDisabledDays({
      ...dayPickerProps?.disabledDays,
      before: hasValidPreventDatesBefore
        ? preventDatesBefore
        : (dayPickerProps?.disabledDays as BeforeModifier)?.before,
      after:
        (dayPickerProps?.disabledDays as AfterModifier)?.after < today
          ? (dayPickerProps?.disabledDays as AfterModifier)?.after
          : today,
    });
  }, [preventFutureDate, dayPickerProps?.disabledDays, preventDatesBefore]);

  const _dayPickerProps: DayPickerProps = {
    // eslint-disable-next-line react/display-name
    navbarElement: (navProps: NavbarElementProps): JSX.Element => (
      <NavbarElement {...navProps} preventFutureDate={preventFutureDate} />
    ),
    weekdaysShort: WEEK_DAYS,
    showOutsideDays: true,
    toMonth: preventFutureDate ? today : undefined,
    ...dayPickerProps,
    disabledDays: disabledDays,
  };
  return (
    <DayPickerInput
      value={value}
      onDayChange={(day) => {
        onDateChange && onDateChange(day);
      }}
      dayPickerProps={_dayPickerProps}
      inputProps={{
        id,
        name,
        size,
        isDisabled,
        isInvalid,
        inputRef,
      }}
      component={PickerInput}
    />
  );
};
